import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { ReactFlow, Handle, Position, BezierEdge, SmoothStepEdge, StepEdge, useEdges } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CircleChevronDown } from 'lucide-react';
import { WindowSizeContext } from '/src/App';
import './style.css';


const OurPromise = () => {

    const { width, height, deviceType } = useContext(WindowSizeContext);

    const headlineText = "Our Promise to You";
    const introText = "It can be hard, as a homeowner, to know who to trust when it comes to your home’s climate risks and financial future. Here’s how Quoll operates—and why you can feel confident entering your address.";
    const promiseItems = [
        {
            id: "left-1",
            column: "left",
            position: 1,
            type: "You Deserve the Full Truth",
            description: "We won’t sugarcoat the outlook for your home. Whether the picture looks bright or uncertain, you’ll always get the honest facts—no fluff, no false comfort. You deserve transparency so you can make confident, informed decisions about your property’s future."
        },
        {
            id: "left-2",
            column: "left",
            position: 2,
            type: "There is Always a Way Forward",
            description: "Climate change is complicated—but understanding your risk doesn’t have to be. We’re here to guide you through practical, realistic steps that protect what matters most: your home, your family, and your financial stability."
        },
        {
            id: "left-3",
            column: "left",
            position: 3,
            type: "Your Bottom Line Comes First",
            description: "Every recommendation is made with your wallet in mind. If a simpler or more affordable option works just as well, that’s what we’ll recommend. No upsells. No agenda. Just the best financial path for you."
        },
        {
            id: "right-1",
            column: "right",
            position: 1,
            type: "Your Privacy is Sacred",
            description: "Your data is yours—always. We’ll never sell it, share it, or use it for anything beyond helping you make smarter, data-driven home decisions. You can explore your home’s climate profile safely and privately, with complete control.",
        },
        {
            id: "right-2",
            column: "right",
            position: 2,
            type: "Being Helpful Means Being Available",
            description: "If something’s unclear, we’re here to help. You can book time directly through the platform—no waiting, no chasing, no bots. We’re real people who genuinely care about helping homeowners understand and reduce risk."
        },
        {
            id: "right-3",
            column: "right",
            position: 3,
            type: "Free Today, Free Forever",
            description: "Quoll is—and always will be—completely free. No hidden fees. No gimmicks. We built Quoll to help homeowners make sense of a changing world, not to sell you something. Your free home climate assessment is our commitment to empowering you with knowledge, not transactions."
        }
    ]

    const ourPromiseConclusionText = "See your home’s climate and financial risks—free, private, and science-based. Just enter your address to get your personalized home risk analysis."

    // Get the left column items and sort them by position ascending
    const leftColumnItems = promiseItems.filter(item => item.column === "left").sort((a, b) => a.position - b.position);
    // Get the right column items and sort them by position ascending
    const rightColumnItems = promiseItems.filter(item => item.column === "right").sort((a, b) => a.position - b.position);

    const [selectedPromiseItemId, setSelectedPromiseItemId] = useState(promiseItems[0].id);
    const [showFlow, setShowFlow] = useState(false);
    const [flowReady, setFlowReady] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    const quollAnimalLogo = "https://d2dhzsfgxco53w.cloudfront.net/quoll-logo-business-green-animal-only.png";

    const handleItemClick = (itemId) => {
        setSelectedPromiseItemId(itemId);
    };

    const nodeTypes = useMemo(() => ({
        promiseItemNode: PromiseItemNode,
        quollLogoNode: QuollLogoNode
    }), []);

    const nodeTypeOffesets = {
        promiseItemNode: 0,
        quollLogoNode: 0
    }

    const edgeTypes = useMemo(() => ({
        smoothstep: SmoothStepEdge,
        bezier: BezierEdge
    }), []);

    // Function to get element position relative to the container
    const getElementPosition = useCallback((element) => {
        const container = document.querySelector('.our-promises-container');
        if (!element || !container) return { x: 0, y: 0 };

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return {
            x: elementRect.left - containerRect.left + (elementRect.width / 2),
            y: elementRect.top - containerRect.top + (elementRect.height / 2)
        };
    }, []);

    // Replace only this specific useEffect
    useEffect(() => {
        if (showFlow) {
            setFlowReady(false);
            const timer = setTimeout(() => {
                const { nodes, edges } = getNodesAndEdges();
                if (nodes.length > 0) {
                    setNodes(nodes);
                    // console.log('Nodes created:', nodes);
                }
                if (edges.length > 0) {
                    setEdges(edges);
                    // console.log('Edges created:', edges);
                }
                setFlowReady(true);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [showFlow, selectedPromiseItemId, width, height]);

    const getNodesAndEdges = () => {

        //    Create place for storing each node
        const nodes = [];

        // Map through all items
        promiseItems.forEach(item => {
            // Check to see if is_node is true
            // console.log('ITEM', item);
            // Get the item id, node type and node type id
            const itemId = item.id;

            const newNodeId = `promise-item-${itemId}`;

            // Get the element
            const element = document.getElementById(newNodeId);

            // If the element exists, get the position and create a new node
            if (element) {
                const position = getElementPosition(element);

                let offsetX = 0;
                if (item.column === "left") {
                    if (deviceType === 'tablet') {
                        offsetX = 0;
                    } else {
                        offsetX = 190;
                    }
                } else {
                    if (deviceType === 'tablet') {
                        offsetX = 0;
                    } else {
                        offsetX = -190;
                    }
                }

                // Get the offset for the node type
                const offset = nodeTypeOffesets['promiseItemNode'];
                position.x += offsetX;

                // Create a new node
                const node = {
                    id: newNodeId,
                    type: "promiseItemNode",
                    position: position,
                    data: {
                        isRightSide: item.column === 'right'
                    },
                    visible: true,
                    hidden: false,
                }

                // Add the node to the nodes array
                nodes.push(node);
            } else {
                console.log('ELEMENT NOT FOUND', newNodeId);
            }
        })

        // If there are nodes then add nodes for the house diagram and the supplemental info container
        if (nodes.length > 0) {

            // Get the house diagram element
            const quollLogoElement = document.getElementById('quoll-logo-node');

            // If the house diagram element exists, get the position and create a new node
            if (quollLogoElement) {
                const position = getElementPosition(quollLogoElement);

                // Create a new node
                const node = {
                    id: 'quoll-logo-node',
                    type: 'quollLogoNode',
                    position: position,
                    visible: true,
                    hidden: false,
                    markerEnd: undefined,
                    markerStart: undefined

                }

                nodes.push(node);
            }
        }


        // Edges
        const edges = [];


        // Map through the connections
        promiseItems.forEach(item => {

            // Get the source and target 
            const source = 'quoll-logo-node';
            const target = `promise-item-${item.id}`;
            const colorNotSelected = 'var(--light-grey)';
            const colorSelected = 'var(--page_background)';
            // Check to see if both elements exist
            const sourceElement = document.getElementById(source);
            const targetElement = document.getElementById(target);

            const isSelected = selectedPromiseItemId === item.id;
            const strokeDasharray = isSelected ? 5 : 0;
            const animated = isSelected ? true : false;
            const stroke = isSelected ? colorSelected : colorNotSelected;
            const strokeWidth = isSelected ? 3 : 2;
            // If both elements exist, create an edge
            if (sourceElement && targetElement) {
                edges.push({
                    id: `edge-${source}-${target}`,
                    source: source,
                    target: target,
                    type: 'bezier',
                    pathOptions: {
                        offset: 1
                    },
                    animated: animated,
                    style: {
                        stroke: stroke,
                        strokeWidth: strokeWidth,
                        strokeLinecap: 'round',
                        animation: "dashdraw 0.5s linear infinite", // Adjust the duration here
                        strokeDasharray: strokeDasharray,
                        zIndex: -1
                    }
                });
            }
        })


        // Return the nodes array
        return { nodes, edges };
    }

    // Tiny component that signals when DOM is ready
    const FlowTrigger = ({ onMount }) => {
        useEffect(() => {
            onMount();
        }, []);
        return <div style={{ display: 'none' }} />;
    };

    const renderColumn = (items) => {

        return (
            <div className={`our-promises-container-column ${deviceType}`}>
                {items.map((item, index) => {

                    const isSelected = selectedPromiseItemId === item.id;
                    return (
                        <div
                            className={`our-promise-item ${isSelected ? "selected" : "not-selected"}`} key={index}
                            onClick={isSelected ? () => handleItemClick(item.id) : null}
                        >
                            <div
                                id={`promise-item-${item.id}`}
                                className="our-promise-item-title-container"
                                onClick={!isSelected ? () => handleItemClick(item.id) : null}
                            >
                                <h3 className="our-promise-item-title">{item.type}</h3>
                                {!isSelected && <CircleChevronDown className="our-promise-item-title-icon-chevron" size={20} />}
                            </div>
                            {isSelected && (
                                <div className="our-promise-item-description-container">
                                    <span className="our-promise-item-description text-align-center">{item.description}</span>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    // console.log('showFlow and flowReady', showFlow, flowReady);
    // console.log('nodes and edges', nodes, edges);


    return (
        <div className={`home-page-section-container last-of-type ${deviceType}`}>
            <div className="home-page-section-title">
                <h2>{headlineText}</h2>
            </div>
            <p className="financial-threat-intro-text text-align-center" dangerouslySetInnerHTML={{ __html: introText }} />
            {deviceType === 'mobile' && (
                <>
                    <div className={`our-promises-container ${deviceType}`}>

                        <div className={`our-promises-logo-container ${deviceType}`}>
                            <div className="our-promises-quoll-animal-logo-container">
                                <img
                                    src={quollAnimalLogo}
                                    alt="Quoll Animal Logo"
                                />
                            </div>
                        </div>
                        {renderColumn(promiseItems)}
                    </div>
                </>
            )}

            {deviceType !== 'mobile' && (
                <div className={`our-promises-container ${deviceType}`}>
                    {renderColumn(leftColumnItems)}
                    <div className={`our-promises-logo-container ${deviceType}`}>
                        <div className="our-promises-quoll-animal-logo-container">
                            <img
                                src={quollAnimalLogo}
                                alt="Quoll Animal Logo"
                                id="quoll-logo-node"
                            />
                        </div>
                    </div>
                    {renderColumn(rightColumnItems)}
                    {(showFlow && flowReady && deviceType !== 'mobile') && (
                        <div className="what-if-machine-plan-summary-solving-for-container-flow-container">
                            <ReactFlow
                                nodes={nodes}
                                edges={edges}
                                nodeTypes={nodeTypes}
                                fitView={false}
                                nodesDraggable={false}
                                nodesConnectable={false}
                                zoomOnScroll={false}
                                panOnScroll={false}
                                preventScrolling={true}
                                edgeTypes={edgeTypes}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
            <p className="our-promise-conclusion-text text-align-center" dangerouslySetInnerHTML={{ __html: ourPromiseConclusionText }} />
            <FlowTrigger onMount={() => setShowFlow(true)} />
        </div>
    );
};

export default OurPromise;

// Custom node components - just handles the positioning of the nodes
const QuollLogoNode = () => {
    return (
        <div style={{ width: 1, height: 1, background: 'transparent' }}>
            <Handle type="source" position={Position.Top} style={{ visibility: 'visible' }} />
            <Handle type="source" position={Position.Left} style={{ visibility: 'visible' }} />
            <Handle type="source" position={Position.Right} style={{ visibility: 'visible' }} />
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'visible' }} />
        </div>
    );
};

const PromiseItemNode = ({ data }) => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle
                type="target"
                position={data.isRightSide ? Position.Left : Position.Right}
                style={{ visibility: 'visible' }}
            />
        </div>
    );
};